/**
 * Offline licence verification.
 *
 * Everything here is local: a signature check against a compiled-in public key
 * and a comparison against the clock. There is no activation call, no licence
 * server and nothing to be offline from. That is a deliberate product decision
 * as much as a technical one — a report renderer that stops working because a
 * key server is down is a report renderer nobody puts in a payroll run.
 *
 * The failure modes are all *soft*. An unreadable licence never throws out of a
 * render; it produces a status whose `message` says what is wrong, and the PDF
 * comes out carrying the evaluation mark.
 */
import { verifyAsync } from "@noble/ed25519";
import { base64UrlToBytes, utf8ToBytes } from "./encoding.js";
import { LicenseFormatError, parseLicense } from "./format.js";
import { TRUSTED_KEYS } from "./trusted-keys.js";
import { EVALUATION_PLAN, PRODUCT, type LicensePayload, type LicenseStatus, type TrustedKey } from "./types.js";

/**
 * How long an expired licence keeps working.
 *
 * A renewal that is a week late is an accounts payable problem, not a reason to
 * stamp a customer's month-end reports. Thirty days is long enough to cover a
 * card expiring over a holiday and short enough that it is not a second licence
 * term.
 */
export const DEFAULT_GRACE_PERIOD_DAYS = 30;

/** Where a developer gets a free evaluation key. Printed, never fetched. */
export const EVALUATION_KEY_URL = "https://www.broadpaper.com/eval";

const DAY_MS = 86_400_000;

export interface VerifyOptions {
  /** Fixed clock. Tests and deterministic renders pass one. */
  now?: Date;
  /** Extra keys to trust, on top of the ones built into this SDK. */
  trustedKeys?: readonly TrustedKey[];
  /**
   * Overrides {@link DEFAULT_GRACE_PERIOD_DAYS}. Never applies to an evaluation
   * licence, which has no grace period whatever a host configures.
   */
  gracePeriodDays?: number;
}

/** The status of a caller that supplied no licence at all. */
export function unlicensed(): LicenseStatus {
  return {
    state: "unlicensed",
    licensed: false,
    payload: null,
    plan: null,
    validUntil: null,
    daysRemaining: null,
    message: `BroadPaper is running in evaluation mode. Get a free evaluation key: ${EVALUATION_KEY_URL}`
  };
}

function failure(state: LicenseStatus["state"], message: string, payload: LicensePayload | null = null): LicenseStatus {
  return {
    state,
    licensed: false,
    payload,
    plan: null,
    validUntil: payload?.validUntil != null ? new Date(payload.validUntil * 1000) : null,
    daysRemaining: null,
    message
  };
}

/**
 * Checks a licence string and reports what it entitles the caller to.
 *
 * Never throws for a bad licence: a malformed string, a forged signature and an
 * expired term are all ordinary answers, and a render is not the place to
 * discover that a config file has a typo in it.
 */
export async function verifyLicense(license: string | null | undefined, options: VerifyOptions = {}): Promise<LicenseStatus> {
  if (!license || !license.trim()) return unlicensed();

  let parsed;
  try {
    parsed = parseLicense(license);
  } catch (e) {
    if (e instanceof LicenseFormatError) {
      return failure(
        e.kind,
        e.kind === "unsupported"
          ? `This BroadPaper licence needs a newer version of the SDK (${e.message}). Update @broadpaper/* or the PDF will carry the evaluation watermark.`
          : `The BroadPaper licence could not be read (${e.message}), so PDFs carry the evaluation watermark.`
      );
    }
    /* c8 ignore next -- parseLicense only throws LicenseFormatError */
    throw e;
  }

  const { payload } = parsed;
  if (payload.product !== PRODUCT) {
    return failure(
      "invalid",
      `This licence is for ${JSON.stringify(payload.product)}, not the BroadPaper SDK, so PDFs carry the evaluation watermark.`,
      payload
    );
  }

  const key = findKey(payload.keyId, options.trustedKeys);
  if (!key) {
    return failure(
      "untrusted",
      `This BroadPaper licence was signed with key ${JSON.stringify(payload.keyId)}, which this build does not trust. ` +
        "A licence issued for development is not honoured by a production build; check BROADPAPER_LICENSE points at the right one.",
      payload
    );
  }

  let signatureOk = false;
  try {
    signatureOk = await verifyAsync(parsed.signature, utf8ToBytes(parsed.signedText), base64UrlToBytes(key.publicKey));
  } catch {
    // A malformed public key or a point that is not on the curve. Either way
    // this licence is not going to verify, and a render must not fail over it.
    signatureOk = false;
  }
  if (!signatureOk) {
    return failure("invalid", "This BroadPaper licence failed its signature check — it has been altered or truncated. PDFs carry the evaluation watermark.", payload);
  }

  const now = options.now ?? new Date();

  // A licence with no end date never expires, so there is no clock to consult.
  // Nothing sold today issues one — `readPayload` refuses the only licence type
  // that would — but the field is nullable, and a null that fell through to the
  // arithmetic below would come out as 1970 and watermark a valid licence.
  if (payload.validUntil === null) {
    return { state: "active", licensed: true, payload, plan: payload.plan, validUntil: null, daysRemaining: null, message: null };
  }

  const validUntil = new Date(payload.validUntil * 1000);
  const evaluation = payload.plan === EVALUATION_PLAN;
  // An evaluation licence ends on its date. Paid licences get thirty days
  // because a late renewal is an accounts-payable problem; a free key has no
  // renewal to be late with, and the way to get more time is to ask for it.
  const graceDays = evaluation ? 0 : options.gracePeriodDays ?? DEFAULT_GRACE_PERIOD_DAYS;
  const day = validUntil.toISOString().slice(0, 10);
  const msRemaining = validUntil.getTime() - now.getTime();
  const daysRemaining = Math.floor(msRemaining / DAY_MS);

  if (msRemaining > 0) {
    return {
      state: "active",
      licensed: true,
      payload,
      plan: payload.plan,
      validUntil,
      daysRemaining,
      message: null,
      notice: evaluation
        ? `BroadPaper is running on an evaluation licence for ${payload.organisationName}, which ends on ${day}. ` +
          "It is for evaluation and development, not production use."
        : null
    };
  }

  if (evaluation) {
    return failure(
      "expired",
      `The BroadPaper evaluation licence for ${payload.organisationName} ended on ${day}, so PDFs carry the evaluation watermark again. ` +
        "Need longer? Reply to your licence email or write to hello@broadpaper.com.",
      payload
    );
  }

  if (msRemaining > -graceDays * DAY_MS) {
    const daysOver = Math.max(1, Math.ceil(-msRemaining / DAY_MS));
    return {
      state: "grace",
      licensed: true,
      payload,
      plan: payload.plan,
      validUntil,
      daysRemaining,
      message:
        `The BroadPaper licence for ${payload.organisationName} expired ${daysOver} day${daysOver === 1 ? "" : "s"} ago and is inside its ${graceDays}-day grace period. ` +
        "Renew it and copy the new licence from your account, or PDFs will start carrying the evaluation watermark."
    };
  }

  return failure(
    "expired",
    `The BroadPaper licence for ${payload.organisationName} expired on ${day} and its ${graceDays}-day grace period has passed, ` +
      "so PDFs carry the evaluation watermark. Renewing issues a new licence in your BroadPaper account.",
    payload
  );
}

function findKey(keyId: string, extra: readonly TrustedKey[] | undefined): TrustedKey | undefined {
  // Runtime keys are searched first so a development key can shadow a built-in
  // one of the same name without a rebuild.
  return extra?.find((k) => k.keyId === keyId) ?? TRUSTED_KEYS.find((k) => k.keyId === keyId);
}
